import React, { useState, useEffect } from 'react';
import { format, setMonth, setYear, getMonth, getYear } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
} from '../ui/drawer';
import { cn } from '../../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

// Hook to detect mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

// Months in Portuguese
const MONTHS_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const MONTHS_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const ResponsiveDatePicker = ({
  value,
  onChange,
  placeholder = 'Selecione',
  label,
  disabled,
  locale = 'pt-BR',
  className,
  'data-testid': testId
}) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(value || new Date());
  const dateLocale = locale === 'en-US' ? enUS : ptBR;
  const months = locale === 'en-US' ? MONTHS_EN : MONTHS_PT;

  // Update viewDate when value changes
  useEffect(() => {
    if (value) setViewDate(value);
  }, [value]);

  const formattedValue = value 
    ? format(value, "dd 'de' MMMM, yyyy", { locale: dateLocale })
    : placeholder;

  const handleSelect = (date) => {
    onChange(date);
    setOpen(false);
  };

  const handleMonthChange = (month) => {
    setViewDate(setMonth(viewDate, parseInt(month)));
  };

  const handleYearChange = (year) => {
    setViewDate(setYear(viewDate, parseInt(year)));
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear + i);

  const triggerButton = (
    <Button
      type="button"
      variant="outline"
      className={cn(
        'w-full justify-start text-left font-normal dark:bg-slate-800 dark:border-slate-700 h-11',
        !value && 'text-muted-foreground',
        className
      )}
      data-testid={testId}
    >
      <CalendarIcon className="mr-2 h-4 w-4 text-orange-500" />
      <span className="truncate">{formattedValue}</span>
    </Button>
  );

  // Mobile - use Drawer with better UI
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <div onClick={() => setOpen(true)}>
          {triggerButton}
        </div>
        <DrawerContent className="max-h-[85vh]">
          <div className="mx-auto w-full max-w-sm">
            {/* Header with month/year selectors */}
            <div className="px-4 pt-4 pb-2">
              <p className="text-sm font-medium text-center text-slate-500 mb-3">
                {label || 'Selecionar data'}
              </p>
              <div className="flex items-center justify-between gap-2 mb-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setViewDate(setMonth(viewDate, getMonth(viewDate) - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                <div className="flex gap-2 flex-1">
                  <Select
                    value={getMonth(viewDate).toString()}
                    onValueChange={handleMonthChange}
                  >
                    <SelectTrigger className="flex-1 h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((month, idx) => (
                        <SelectItem key={idx} value={idx.toString()}>
                          {month}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select
                    value={getYear(viewDate).toString()}
                    onValueChange={handleYearChange}
                  >
                    <SelectTrigger className="w-24 h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setViewDate(setMonth(viewDate, getMonth(viewDate) + 1))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Calendar */}
            <div className="px-2 flex justify-center">
              <Calendar
                mode="single"
                selected={value}
                onSelect={handleSelect}
                month={viewDate}
                onMonthChange={setViewDate}
                disabled={disabled}
                locale={dateLocale}
                className="rounded-md"
                classNames={{
                  months: "flex flex-col",
                  month: "space-y-2",
                  caption: "hidden",
                  nav: "hidden",
                  table: "w-full border-collapse",
                  head_row: "flex justify-around",
                  head_cell: "text-slate-500 rounded-md w-9 font-normal text-[0.8rem]",
                  row: "flex w-full mt-1 justify-around",
                  cell: "text-center text-sm p-0 relative",
                  day: cn(
                    "h-10 w-10 p-0 font-normal rounded-full",
                    "hover:bg-orange-100 dark:hover:bg-orange-900/30",
                    "focus:bg-orange-100 dark:focus:bg-orange-900/30"
                  ),
                  day_selected: "bg-orange-500 text-white hover:bg-orange-600 focus:bg-orange-600",
                  day_today: "bg-slate-100 dark:bg-slate-800 font-semibold",
                  day_outside: "text-slate-300 dark:text-slate-600",
                  day_disabled: "text-slate-300 dark:text-slate-600 cursor-not-allowed",
                }}
              />
            </div>

            {/* Selected date preview */}
            {value && (
              <div className="px-4 py-2 text-center">
                <p className="text-sm text-slate-500">Data selecionada:</p>
                <p className="text-lg font-semibold text-orange-500">
                  {format(value, "EEEE, dd 'de' MMMM", { locale: dateLocale })}
                </p>
              </div>
            )}
          </div>
          
          <DrawerFooter className="pt-2 pb-6">
            <Button 
              onClick={() => setOpen(false)}
              className="w-full rounded-full bg-orange-500 hover:bg-orange-600"
            >
              Confirmar
            </Button>
            <DrawerClose asChild>
              <Button variant="outline" className="w-full rounded-full">
                Cancelar
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop - use Popover
  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        {triggerButton}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-50" align="start" sideOffset={5}>
        <Calendar
          mode="single"
          selected={value}
          onSelect={handleSelect}
          disabled={disabled}
          locale={dateLocale}
        />
      </PopoverContent>
    </Popover>
  );
};

export default ResponsiveDatePicker;
